import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  eWayBillViewAction,
  eWayBillMastersAction,
  eWayBillAuthTokenAction,
  eWayBillUpdateTransporterAction,
  eWayBillCancelAction,
  eWayBillRejectAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import moment from "moment";
import { Truck, Train, Plane, Ship, ChevronDown, ChevronUp, X } from "lucide-react";
import LoadingSpinner from "../../helper-components/loading-spinner";

/* ─── constants ──────────────────────────────────────────────────────────── */
const MODE_ICONS   = { "1": Truck, "2": Train, "3": Plane, "4": Ship };
const MODE_LABELS  = { "1": "Road", "2": "Rail", "3": "Air",  "4": "Ship" };
const VEHICLE_LABELS = { R: "Regular", O: "ODC" };

const LW = "10rem";
const LABEL_CLS =
  "whitespace-nowrap flex-shrink-0 text-[var(--input-label-color)] " +
  "text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]";
const VALUE_CLS =
  "flex-1 min-h-[38px] rounded-[8px] bg-[var(--input-bg)] border " +
  "border-[var(--input-border)] px-3 flex items-center text-[var(--input-color)] " +
  "text-[length:var(--input-font-size)] font-[var(--input-font-weight)]";
const INPUT_CLS =
  "flex-1 min-h-[38px] rounded-[8px] bg-white border " +
  "border-[var(--input-border)] px-3 text-[var(--input-color)] " +
  "text-[length:var(--input-font-size)] font-[var(--input-font-weight)] outline-none " +
  "focus:border-[var(--primary)]";
/* ─── helpers ────────────────────────────────────────────────────────────── */
const resolveLabel = (arr = [], val) => {
  if (!val) return val ?? "—";
  const found = arr.find(
    (item) => String(item.value ?? item.code ?? "") === String(val)
  );
  return found?.name ?? found?.label ?? val;
};

const toOptions = (arr = []) =>
  arr.map((item) => ({
    label: item?.name ?? item?.label ?? "",
    value: String(item?.value ?? item?.code ?? ""),
  }));

/* ─── sub-components ─────────────────────────────────────────────────────── */
const ViewField = ({ label, value }) => (
  <div className="flex items-center w-full">
    <span className={LABEL_CLS} style={{ minWidth: LW }}>{label}</span>
    <div className={VALUE_CLS}>{value || "—"}</div>
  </div>
);


const AddressCol = ({ title, lines = [], gstin }) => (
  <div className="flex-1 min-w-0">
    <p
      className="font-[600] text-[var(--nav-heading-color)] uppercase mb-2"
      style={{ fontSize: "11px", letterSpacing: "0.05em" }}
    >
      {title}
    </p>
    {gstin && (
      <p className="mb-1">
        <span
          className="inline-block rounded px-[6px] py-[2px] font-[600] bg-[#eaf1fb] text-[var(--primary)] border border-[#c3d8f5]"
          style={{ fontSize: "11px" }}
        >
          GSTIN: {gstin}
        </span>
      </p>
    )}
    {lines.filter(Boolean).length > 0 ? (
      lines.filter(Boolean).map((line, i) => (
        <p key={i} className="text-[var(--secondary)]" style={{ fontSize: "12px", lineHeight: "1.7" }}>
          {line}
        </p>
      ))
    ) : (
      <p className="text-[var(--placeholder-color)]" style={{ fontSize: "12px" }}>—</p>
    )}
  </div>
);

const HeaderChip = ({ label, value }) => (
  <div className="flex flex-col gap-[3px]">
    <span
      className="text-[var(--search-input-label-color)] font-[var(--search-label-font-weight)]"
      style={{ fontSize: "var(--search-label-font-size, 12px)" }}
    >
      {label}
    </span>
    <div
      className="border border-[var(--search-input-border)] rounded-[8px] px-3
        bg-[var(--search-input-bg)] text-[var(--search-input-color)]
        font-[var(--search-input-font-weight)] flex items-center"
      style={{ minHeight: "38px", fontSize: "var(--search-input-font-size, 13px)", minWidth: "9rem" }}
    >
      {value || "—"}
    </div>
  </div>
);

/* ─── Cancel Modal ───────────────────────────────────────────────────────── */
const CancelModal = ({ onClose, onConfirm, reasonOptions, saving }) => {
  const [reason, setReason]   = useState("");
  const [remark, setRemark]   = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}>
      <div
        className="bg-white rounded-[12px] shadow-xl w-full max-w-[440px] mx-4"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--light-border-color)]">
          <span className="font-[700] text-[var(--secondary)]" style={{ fontSize: "14px" }}>
            Cancel E-Way Bill
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--placeholder-color)] hover:text-[var(--secondary)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* body */}
        <div className="px-5 py-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label
              className="text-[var(--input-label-color)] font-[var(--input-label-font-weight)]"
              style={{ fontSize: "var(--input-label-font-size, 13px)" }}
            >
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <select
              className="min-h-[38px] rounded-[8px] bg-white border border-[var(--input-border)] px-3
                text-[var(--input-color)] text-[length:var(--input-font-size)] outline-none
                focus:border-[var(--primary)]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Select reason…</option>
              {reasonOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label
              className="text-[var(--input-label-color)] font-[var(--input-label-font-weight)]"
              style={{ fontSize: "var(--input-label-font-size, 13px)" }}
            >
              Remarks
            </label>
            <textarea
              rows={3}
              className="rounded-[8px] bg-white border border-[var(--input-border)] px-3 py-2
                text-[var(--input-color)] text-[length:var(--input-font-size)] outline-none
                focus:border-[var(--primary)] resize-none"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remark…"
            />
          </div>
        </div>

        {/* footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--light-border-color)]">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-[var(--draft-button-bg)] cursor-pointer
              text-[var(--draft-button-fg)]
              border border-[var(--draft-button-border)]
              text-[length:var(--draft-button-fs)]
              font-[var(--draft-button-fw)]"
          >
            Close
          </button>
          <button
            type="button"
            disabled={!reason || saving}
            onClick={() => onConfirm({ reason, remark })}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-F94B4B text-white border border-F94B4B
              font-[var(--primary-button-font-weight)]
              text-[length:var(--primary-button-create-fs)]
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Cancelling…" : "Confirm Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Reject Modal ───────────────────────────────────────────────────────── */
const RejectModal = ({ onClose, onConfirm, saving }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}>
    <div
      className="bg-white rounded-[12px] shadow-xl w-full max-w-[400px] mx-4"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--light-border-color)]">
        <span className="font-[700] text-[var(--secondary)]" style={{ fontSize: "14px" }}>
          Reject E-Way Bill
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--placeholder-color)] hover:text-[var(--secondary)]"
        >
          <X size={18} />
        </button>
      </div>

      {/* body */}
      <div className="px-5 py-6">
        <p
          className="text-[var(--secondary)]"
          style={{ fontSize: "13px", lineHeight: "1.6" }}
        >
          Are you sure you want to reject this E-Way Bill? This action cannot be undone.
        </p>
      </div>

      {/* footer */}
      <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--light-border-color)]">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
            bg-[var(--draft-button-bg)] cursor-pointer
            text-[var(--draft-button-fg)]
            border border-[var(--draft-button-border)]
            text-[length:var(--draft-button-fs)]
            font-[var(--draft-button-fw)]"
        >
          Close
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onConfirm}
          className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
            bg-F94B4B text-white border border-F94B4B
            font-[var(--primary-button-font-weight)]
            text-[length:var(--primary-button-create-fs)]
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Rejecting…" : "Confirm Reject"}
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const ViewEWayBill = ({ invoiceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ── loading ── */
  const [pending,  setPending]  = useState(3); // view + masters + auth token
  const [loading,  setLoading]  = useState(true);

  /* ── data ── */
  const [ewbData,  setEwbData]  = useState(null);
  const [items,    setItems]    = useState([]);
  const [masters,  setMasters]  = useState({});
  const [authToken, setAuthToken] = useState("");

  /* ── UI toggles ── */
  const [itemOpen, setItemOpen] = useState(true);

  /* ── Transporter ID edit ── */
  const [transporterEdit, setTransporterEdit] = useState("");
  const [transporterSaving, setTransporterSaving] = useState(false);

  /* ── Cancel modal ── */
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSaving,    setCancelSaving]    = useState(false);

  /* ── Reject modal ── */
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectSaving,    setRejectSaving]    = useState(false);

  const decrement = () =>
    setPending((prev) => {
      const next = prev - 1;
      if (next <= 0) setLoading(false);
      return next;
    });

  useEffect(() => {
    /* 1. auth token */
    dispatch(
      eWayBillAuthTokenAction(
        (res) => {
          const token =
            res?.data?.authenticationToken ??
            res?.authenticationToken ??
            res?.data?.AuthenticationToken ??
            "";
          setAuthToken(token);
          decrement();
        },
        () => decrement()
      )
    );

    /* 2. stored EWB details */
    dispatch(
      eWayBillViewAction(
        { soit_id: invoiceId },
        (res) => {
          const d = res?.data ?? res ?? {};
          const b = d?.eway_bill ?? d;
          setEwbData(b);
          setItems(d?.items ?? []);
          setTransporterEdit(b?.transporterid ?? "");
          decrement();
        },
        (err) => {
          errorToast(err?.data?.message ?? "Failed to fetch E-Way Bill details");
          decrement();
        }
      )
    );

    /* 3. masters (dropdown labels) */
    dispatch(
      eWayBillMastersAction(
        (res) => {
          const m = res?.data ?? res ?? {};
          setMasters({
            docType:              m.docType              ?? [],
            subSupplyType:        m.subSupplyType        ?? [],
            transactionType:      m.transactionType      ?? [],
            states:               m.states               ?? [],
            transMode:            m.transMode            ?? [],
            vehicleType:          m.vehicleType          ?? [],
            cancellationReason:   m.cancellationReason   ?? [],
          });
          decrement();
        },
        () => decrement()
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  /* ── Transporter save ── */
  const handleSaveTransporter = () => {
    if (!transporterEdit.trim()) return;
    setTransporterSaving(true);
    dispatch(
      eWayBillUpdateTransporterAction(
        {
          authenticationToken: authToken,
          updateItems: [{
            soit_id:       invoiceId,
            ewbNo:         String(ewbData?.ewbno ?? ""),
            transporterId: transporterEdit.trim(),
          }],
        },
        (data) => {
          setTransporterSaving(false);
          if (data?.status === false) {
            errorToast(data?.message ?? "Failed to update transporter");
          } else {
            successToast(data?.message ?? "Transporter updated successfully");
            setEwbData((prev) => ({ ...prev, transporterid: transporterEdit.trim() }));
          }
        },
        (err) => {
          setTransporterSaving(false);
          errorToast(err?.data?.message ?? "Failed to update transporter");
        }
      )
    );
  };

  /* ── Cancel ── */
  const handleCancel = ({ reason, remark }) => {
    setCancelSaving(true);
    dispatch(
      eWayBillCancelAction(
        {
          authenticationToken: authToken,
          cancelItems: [{
            soit_id:       invoiceId,
            eWayBillNo:    String(ewbData?.ewbno ?? ""),
            CanReasonCode: reason,
            CanRemark:     remark,
          }],
        },
        (data) => {
          setCancelSaving(false);
          setShowCancelModal(false);
          if (data?.status === false) {
            errorToast(data?.message ?? "Failed to cancel E-Way Bill");
          } else {
            successToast(data?.message ?? "E-Way Bill cancelled successfully");
            navigate("/e-way-bills");
          }
        },
        (err) => {
          setCancelSaving(false);
          errorToast(err?.data?.message ?? "Failed to cancel E-Way Bill");
        }
      )
    );
  };

  /* ── Reject ── */
  const handleReject = () => {
    setRejectSaving(true);
    dispatch(
      eWayBillRejectAction(
        {
          authenticationToken: authToken,
          rejectItems: [{
            soit_id: invoiceId,
            ewbNo:   Number(ewbData?.ewbno ?? 0),
          }],
        },
        (data) => {
          setRejectSaving(false);
          setShowRejectModal(false);
          if (data?.status === false) {
            errorToast(data?.message ?? "Failed to reject E-Way Bill");
          } else {
            successToast(data?.message ?? "E-Way Bill rejected successfully");
            navigate("/e-way-bills");
          }
        },
        (err) => {
          setRejectSaving(false);
          setShowRejectModal(false);
          errorToast(err?.data?.message ?? "Failed to reject E-Way Bill");
        }
      )
    );
  };

  if (loading) return <LoadingSpinner />;

  const b = ewbData ?? {};

  /* ── resolve human-readable labels from masters ── */
  const docTypeLabel  = resolveLabel(masters.docType,         b.doctype);
  const subTypeLabel  = resolveLabel(masters.subSupplyType,   b.subsupplytype);
  const txnTypeLabel  = resolveLabel(masters.transactionType, b.transactiontype);
  const podLabel      = resolveLabel(masters.states,          b.shiptostatecode);

  /* ── header ── */
  const custName  = b.totrdname ?? "—";
  const docNo     = b.docno     ?? "—";
  const docDate   = b.docdate   ?? "";
  const ewbNo     = b.ewbno;
  const validUpto = b.validupto;

  /* ── address lines ── */
  const fromLines = [
    b.fromtrdname,
    b.fromaddr1,
    b.fromaddr2,
    b.fromplace,
    b.frompincode ? `Pincode: ${b.frompincode}` : null,
  ];
  const toLines = [
    b.totrdname,
    b.toaddr1,
    b.toaddr2,
    b.toplace,
    b.topincode ? `India - ${b.topincode}` : null,
  ];

  /* ── totals ── */
  const taxableTotal = parseFloat(b.totalvalue  ?? 0);
  const sgstVal      = parseFloat(b.sgstvalue   ?? 0);
  const cgstVal      = parseFloat(b.cgstvalue   ?? 0);
  const igstVal      = parseFloat(b.igstvalue   ?? 0);
  const gstTotal     = sgstVal + cgstVal + igstVal;
  const grandTotal   = parseFloat(b.totinvvalue ?? 0);

  /* ── transport ── */
  const transMode     = String(b.transmode    ?? "");
  const transDist     = b.transdistance       ?? "";
  const vehicleNo     = b.vehicleno           ?? "";
  const vehicleType   = b.vehicletype         ?? "";
  const transDocNo    = b.transdocno          ?? "";
  const transDocDate  = b.transdocdate        ?? "";

  /* ── dropdown options ── */
  const cancelReasonOpts = toOptions(masters.cancellationReason ?? []);

  const ModeIcon = MODE_ICONS[transMode] ?? Truck;

  return (
    <>
      {showCancelModal && (
        <CancelModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancel}
          reasonOptions={cancelReasonOpts}
          saving={cancelSaving}
        />
      )}

      {showRejectModal && (
        <RejectModal
          onClose={() => setShowRejectModal(false)}
          onConfirm={handleReject}
          saving={rejectSaving}
        />
      )}

      <div className="h-full flex flex-col">

        {/* ════ SCROLLABLE BODY ══════════════════════════════════════════════ */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Header bar ────────────────────────────────────────────────── */}
          <div
            className="bg-[var(--add-billing-head-bg)] px-6 flex items-center gap-5 flex-wrap"
            style={{ minHeight: "5.5rem", borderBottom: "1px solid var(--light-border-color)" }}
          >
            <HeaderChip label="Document Type"        value={docTypeLabel} />
            <HeaderChip label="Transaction Sub Type" value={subTypeLabel} />
            <HeaderChip label="Customer Name"        value={custName} />
            <HeaderChip label="Invoice #"            value={docNo} />
            {docDate && <HeaderChip label="Date"     value={docDate} />}

            {ewbNo ? (
              <div className="ml-auto flex flex-col gap-[3px]">
                <span
                  className="text-[var(--search-input-label-color)] font-[var(--search-label-font-weight)]"
                  style={{ fontSize: "var(--search-label-font-size, 12px)" }}
                >
                  EWB Number
                </span>
                <div
                  className="rounded-[8px] px-3 flex items-center font-[600] bg-[#e6f7ee] text-[#1a7a45] border border-[#a3d9bc]"
                  style={{ minHeight: "38px", fontSize: "13px" }}
                >
                  {ewbNo}
                  {validUpto && (
                    <span className="ml-3 text-[var(--placeholder-color)] font-[400]" style={{ fontSize: "11px" }}>
                      Valid upto:{" "}
                      {moment(validUpto).isValid()
                        ? moment(validUpto).format("DD MMM YYYY")
                        : validUpto}
                    </span>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* ════ BODY ═══════════════════════════════════════════════════════ */}
          <div className="px-6 py-5 flex flex-col gap-0">

            {/* ── Section 1 ────────────────────────────────────────────────── */}
            <section className="pb-5 border-b border-[var(--light-border-color)] flex flex-col gap-4">
              <ViewField label="Transaction Type" value={txnTypeLabel} />

              {b.subsupplydesc && (
                <ViewField label="Sub Supply Desc" value={b.subsupplydesc} />
              )}

              {/* Address Details */}
              <div className="flex items-start">
                <span className={LABEL_CLS} style={{ minWidth: LW }}>Address Details</span>
                <div className="flex gap-6 flex-1 border border-[var(--input-border)] rounded-[8px] p-4 bg-[var(--input-bg)]">
                  <AddressCol title="Dispatch From" lines={fromLines} gstin={b.fromgstin} />
                  <div className="w-px bg-[var(--light-border-color)]" />
                  <AddressCol title="Bill From"     lines={fromLines} gstin={b.fromgstin} />
                  <div className="w-px bg-[var(--light-border-color)]" />
                  <AddressCol title="Bill To"       lines={toLines}   gstin={b.togstin !== "URP" ? b.togstin : null} />
                  <div className="w-px bg-[var(--light-border-color)]" />
                  <AddressCol title="Ship To"       lines={toLines}   gstin={b.togstin !== "URP" ? b.togstin : null} />
                </div>
              </div>

              <ViewField label="Place of Delivery" value={podLabel} />
            </section>

            {/* ── Section 2: Item Details ────────────────────────────────── */}
            <section className="py-5 border-b border-[var(--light-border-color)]">
              <button
                type="button"
                onClick={() => setItemOpen((v) => !v)}
                className="flex items-center gap-2 mb-3 text-[var(--primary)]"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                <span className="font-[600] text-[length:var(--input-label-font-size)]">Item Details</span>
                {itemOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>

              {itemOpen && (
                <div>
                  <div className="overflow-x-auto border border-[var(--light-border-color)] rounded-[8px]">
                    <table className="w-full" style={{ fontSize: "13px" }}>
                      <thead>
                        <tr className="bg-[var(--create-table-header-bg)] text-white">
                          {["#", "Item & Description", "HSN Code", "Quantity", "Taxable Amount", "SGST %", "CGST %", "IGST %"].map((h) => (
                            <th key={h} className="text-left px-4 py-[10px] font-[500]" style={{ fontSize: "12px" }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.length > 0 ? (
                          items.map((item, idx) => (
                            <tr key={idx} className="border-t border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]">
                              <td className="px-4 py-3 text-[var(--secondary)]">{idx + 1}</td>
                              <td className="px-4 py-3 text-[var(--secondary)] font-[500]">
                                {item.item_name ?? "—"}
                              </td>
                              <td className="px-4 py-3 text-[var(--secondary)]">{item.hsncode ?? "—"}</td>
                              <td className="px-4 py-3 text-[var(--secondary)]">{item.quantity ?? "—"}</td>
                              <td className="px-4 py-3 text-[var(--secondary)]">
                                ₹{parseFloat(item.taxableamount ?? 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-[var(--secondary)]">
                                <span className="text-[var(--placeholder-color)]">{item.sgstrate ?? 0}%</span>
                              </td>
                              <td className="px-4 py-3 text-[var(--secondary)]">
                                <span className="text-[var(--placeholder-color)]">{item.cgstrate ?? 0}%</span>
                              </td>
                              <td className="px-4 py-3 text-[var(--secondary)]">
                                <span className="text-[var(--placeholder-color)]">{item.igstrate ?? 0}%</span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-t border-[var(--light-border-color)]">
                            <td className="px-4 py-3 text-center text-[var(--placeholder-color)]" colSpan={8}>
                              No items available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="flex justify-end mt-3">
                    <div
                      className="border border-[var(--light-border-color)] rounded-[8px] p-4 bg-[var(--input-bg)]"
                      style={{ minWidth: "280px", fontSize: "13px" }}
                    >
                      <div className="flex justify-between mb-2">
                        <span className="text-[var(--nav-heading-color)]">Taxable Amount</span>
                        <span className="text-[var(--secondary)] font-[500]">₹{taxableTotal.toFixed(2)}</span>
                      </div>
                      {gstTotal > 0 && (
                        <div className="flex justify-between mb-2">
                          <span className="text-[var(--nav-heading-color)]">GST</span>
                          <span className="text-[var(--secondary)] font-[500]">₹{gstTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-[var(--light-border-color)] pt-2 mt-2">
                        <span className="font-[700] text-[var(--secondary)]">TOTAL</span>
                        <span className="font-[700] text-[var(--secondary)]">₹{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ── Section 3: Transportation Details + Part B ─────────────── */}
            <section className="py-5">
              <div className="grid grid-cols-2 gap-10">

                {/* LEFT — Transportation Details */}
                <div className="flex flex-col gap-5">
                  <h3 className="font-[700] text-[var(--secondary)] uppercase" style={{ fontSize: "12px", letterSpacing: "0.06em" }}>
                    Transportation Details
                  </h3>

                  {/* Transporter ID — editable with inline Save */}
                  <div className="flex items-center w-full gap-2">
                    <span className={LABEL_CLS} style={{ minWidth: LW }}>Transporter ID</span>
                    <input
                      className={INPUT_CLS}
                      value={transporterEdit}
                      onChange={(e) => setTransporterEdit(e.target.value)}
                      placeholder="e.g. 05AAAAU5634K1ZD"
                    />
                    <button
                      type="button"
                      disabled={transporterSaving || !transporterEdit.trim()}
                      onClick={handleSaveTransporter}
                      className="flex items-center px-[20px] h-[38px] whitespace-nowrap rounded-md
                        bg-[var(--primary-button-bg-color)] cursor-pointer
                        text-[var(--primary-button-fg-color)]
                        border border-[var(--primary-button-bg-color)]
                        hover:bg-[var(--primary-button-hover-color)]
                        text-[length:var(--primary-button-create-fs)]
                        font-[var(--primary-button-font-weight)]
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {transporterSaving ? "…" : "Save"}
                    </button>
                  </div>

                  <ViewField label="Distance (in Km)" value={transDist ? String(transDist) : "—"} />
                </div>

                {/* RIGHT — Part B (read-only) */}
                <div className="flex flex-col gap-5">
                  <h3 className="font-[700] text-[var(--secondary)] uppercase" style={{ fontSize: "12px", letterSpacing: "0.06em" }}>
                    Part B
                  </h3>

                  {/* Mode of Transportation */}
                  <div className="flex items-center">
                    <span className={LABEL_CLS} style={{ minWidth: LW }}>Mode of Transportation</span>
                    {transMode ? (
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-[8px] border border-[var(--primary)] text-[var(--primary)] bg-[#e6f1ff] font-[500]"
                        style={{ fontSize: "13px" }}
                      >
                        <ModeIcon size={14} />
                        {MODE_LABELS[transMode] ?? transMode}
                      </div>
                    ) : (
                      <span className="text-[var(--placeholder-color)]">—</span>
                    )}
                  </div>

                  {/* Vehicle Type */}
                  <div className="flex items-center">
                    <span className={LABEL_CLS} style={{ minWidth: LW }}>Vehicle Type</span>
                    <div className="flex items-center gap-6">
                      {["R", "O"].map((v) => (
                        <label key={v} className="flex items-center gap-2 text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--input-font-weight)]">
                          <input
                            type="radio"
                            readOnly
                            checked={vehicleType === v}
                            onChange={() => {}}
                            className="accent-[var(--primary)] w-4 h-4"
                          />
                          {VEHICLE_LABELS[v]}
                        </label>
                      ))}
                    </div>
                  </div>

                  <ViewField label="Vehicle No"             value={vehicleNo} />
                  <ViewField label="Transporter's Doc No"   value={transDocNo} />
                  <ViewField label="Transporter's Doc Date" value={transDocDate} />
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* ════ STICKY FOOTER ════════════════════════════════════════════════ */}
        <div
          className="border-t border-[var(--create-table-footer-border)] bg-white h-[4rem] flex justify-between items-center px-5"
          style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.04)" }}
        >
          <div />
          <div className="flex items-center gap-3">
            {/* Cancel EWB */}
            <button
              type="button"
              disabled={cancelSaving}
              onClick={() => setShowCancelModal(true)}
              className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
                text-F94B4B border border-F94B4B cursor-pointer
                font-[var(--primary-button-font-weight)]
                text-[length:var(--primary-button-create-fs)]
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel E-Way Bill
            </button>

            {/* Reject EWB */}
            <button
              type="button"
              disabled={rejectSaving}
              onClick={() => setShowRejectModal(true)}
              className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
                bg-F94B4B text-white border border-F94B4B cursor-pointer
                font-[var(--primary-button-font-weight)]
                text-[length:var(--primary-button-create-fs)]
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject E-Way Bill
            </button>

            {/* Back */}
            <button
              type="button"
              onClick={() => navigate("/e-way-bills")}
              className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
                bg-[var(--draft-button-bg)] cursor-pointer
                text-[var(--draft-button-fg)]
                border border-[var(--draft-button-border)]
                text-[length:var(--draft-button-fs)]
                font-[var(--draft-button-fw)]"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewEWayBill;
